/* Lightweight SEO hook — sets document.title + <meta> tags + structured-data <script>.
 * Avoids adding the react-helmet dependency.
 */
import { useEffect } from 'react';

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertCanonical = (url) => {
  if (!url) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
};

const upsertJsonLd = (id, data) => {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-jsonld', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const useSEO = ({ title, description, keywords, image, jsonLd, canonical }) => {
  useEffect(() => {
    const fullTitle = title ? `${title} | TelanganaMart` : 'TelanganaMart — Telangana\'s Largest B2B Marketplace';
    document.title = fullTitle;
    setMeta('description', description || 'Find verified Telangana vendors, manufacturers and wholesalers. Get the best B2B quotes within minutes.');
    if (keywords) setMeta('keywords', keywords);
    setMeta('og:title', fullTitle, 'property');
    if (description) setMeta('og:description', description, 'property');
    if (image) setMeta('og:image', image, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:url', canonical || window.location.href.split('?')[0], 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    if (description) setMeta('twitter:description', description);
    if (image) setMeta('twitter:image', image);
    upsertCanonical(canonical || window.location.href.split('?')[0]);

    // jsonLd can be a single object OR an array of objects. Each array entry
    // gets its own <script> slot keyed by index so we can render Product +
    // Breadcrumb (and more) together without clobbering each other.
    const blocks = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    blocks.forEach((b, i) => upsertJsonLd(`page-${i}`, b));
    // Clean up extra slots if the new list is shorter than before.
    document.head.querySelectorAll('script[data-jsonld^="page-"]').forEach((el) => {
      const idx = parseInt((el.getAttribute('data-jsonld') || '').replace('page-', ''), 10);
      if (Number.isFinite(idx) && idx >= blocks.length) el.remove();
    });
    upsertJsonLd('page', null);  // remove legacy single-slot key if any

    return () => {
      document.head.querySelectorAll('script[data-jsonld^="page-"]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, image, canonical, JSON.stringify(jsonLd || {})]);
};

export default useSEO;
